import React, { useState } from "react";
import { useSelector } from "react-redux";
import DecisionMakerCard from "../../../components/decisionMakerCard/DecisionMakerCard";
import {
  emailTemplate,
  emailTemplateError,
} from "../../../store/selectors/projects/project.selector";
import { toastError, toastSuccess } from "../../../utils/helpers/helper";
import { 
  getAvailableContactsAPI, 
  addContactPreferenceAPI, 
  removeContactPreferenceAPI,
  hideContactPreferenceAPI,
  resetContactPreferencesAPI
} from "../../../utils/requests/callLogs";
import { sendEmailToContactAPI } from "../../../utils/requests/projects";
import DomainVerificationModal from "../../../components/DomainVerificationModal";
import { Button, Modal, ListGroup, Badge, Form, InputGroup } from "react-bootstrap";
import { FaPlus, FaMinus, FaUserPlus, FaSearch, FaUndo } from "react-icons/fa";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "./DecisionMakers.css";

dayjs.extend(relativeTime);

const DecisionMakers = ({
  emailTemplates,
  handleSelectedTemplate,
  projectId,
  callLogHandler,
  contactRoles,
  getContactRoles,
  projectName,
  account_name,
  account_phone,
  updateProjectLastDate,
  contactActivities,
  createdByUsers = false
}) => {
  const templateError = useSelector(emailTemplateError());
  const emailTemplateBody = useSelector(emailTemplate());
  const { user } = useSelector((state) => state.userDetails);
  const [verificationModalShow, setVerificationModalShow] = useState(false);
  const [manageContactsModalShow, setManageContactsModalShow] = useState(false);
  const [resetConfirmModalShow, setResetConfirmModalShow] = useState(false);
  const [availableContacts, setAvailableContacts] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchAvailableContacts = () => {
    if (!projectId) return;
    setLoadingContacts(true);
    getAvailableContactsAPI(projectId)
      .then((response) => {
        setAvailableContacts(response.contacts || []);
        setCompanyName(response.company_name || "");
      })
      .catch((error) => {
        console.error("Error fetching available contacts:", error);
        toastError("Failed to fetch available contacts");
      })
      .finally(() => {
        setLoadingContacts(false);
      });
  };

  const handleOpenManageContacts = () => {
    setManageContactsModalShow(true);
    fetchAvailableContacts();
  };

  const handleAddContact = (contactId, contactType = 'company', is_in_contact_roles = false) => {
    addContactPreferenceAPI(projectId, contactId, contactType, is_in_contact_roles)
      .then(() => {
        toastSuccess("Contact added successfully");
        fetchAvailableContacts();
        getContactRoles();
      })
      .catch((error) => {
        toastError("Failed to add contact");
      });
  };

  const handleShowContact = (contact) => {
    if (createdByUsers) {
      handleAddContact(contact.id, 'deal', contact.is_in_contact_roles);
    } else {
      handleAddContact(contact.contact_id, 'company', contact.is_in_contact_roles);
    }
  };

  const handleHideContact = (contactId, contactType = 'company') => {
    hideContactPreferenceAPI(projectId, contactId, contactType)
      .then(() => {
        toastSuccess("Contact hidden successfully");
        fetchAvailableContacts();
        getContactRoles();
      })
      .catch((error) => {
        toastError("Failed to hide contact");
      });
  };

  const handleRemoveContact = (contactId, contactRoleId = null) => {
    removeContactPreferenceAPI(projectId, contactId, 'company', contactRoleId)
      .then(() => {
        toastSuccess("Contact removed successfully");
        fetchAvailableContacts();
        getContactRoles();
      })
      .catch((error) => {
        toastError("Failed to remove contact");
      });
  };

  const handleResetToOriginal = () => {
    setResetConfirmModalShow(true);
    setManageContactsModalShow(false);
  };

  const confirmResetToOriginal = () => {
    resetContactPreferencesAPI(projectId)
      .then(() => {
        toastSuccess("Reset to original contacts successfully");
        fetchAvailableContacts();
        getContactRoles();
        setResetConfirmModalShow(false);
        setManageContactsModalShow(false);
      })
      .catch((error) => {
        toastError("Failed to reset preferences");
      });
  };

  const handleHideContactFromList = (contact) => {
    if (contact.is_added && !contact.is_in_contact_roles) {
      handleRemoveContact(`${contact.id}`, contact.contact_id);
      return;
    }
    let contactType = 'company';
    let contactId = contact.contact_id;
    
    if (createdByUsers) {
      contactType = 'deal';
      contactId = contact.id;
    } else if (contact.contact_role_id) {
      contactType = 'contact_role';
      contactId = contact.contact_role_id;
    }
    
    handleHideContact(contactId, contactType);
  };

  // const sendEmailHandler = (
  //   contact,
  //   selectedTemplate,
  //   subject,
  //   body,
  //   handleCloseEmailModal,
  //   image
  // ) => {
  //   if (!user?.is_domain_verify) {
  //     setVerificationModalShow(true);
  //     return;
  //   }
  //   let formData = new FormData();
  //   formData.append("temp_id", selectedTemplate?.id);
  //   formData.append("company_id", contact?.company_id);
  //   formData.append("contact_id", contact?.id);
  //   formData.append("subject", subject);
  //   formData.append("body", body);
  //   formData.append("image", image);

  //   sendEmailToContactAPI(projectId, formData)
  //     .then(() => {
  //       toastSuccess("Email Sent Successfully");
  //       handleCloseEmailModal(true);
  //       getContactRoles();
  //     })
  //     .catch((error) => {
  //       toastError(error.data?.message || "something went wrong", {
  //         autoClose: false,
  //       });
  //     });

  //   return;
  // };

  // Filter available contacts based on search term
  const filteredContacts = availableContacts.filter(contact => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (contact.name && contact.name.toLowerCase().includes(searchLower)) ||
      (contact.email && contact.email.toLowerCase().includes(searchLower)) ||
      (contact.title && contact.title.toLowerCase().includes(searchLower))
    );
  });

  const getLastContactLabel = (contact) => {
    const roleContact = contactRoles?.find(
      (c) =>
        c.contact_id === contact.contact_id ||
        String(c.id) === String(contact.id)
    );
    const date = roleContact?.contact_date || contact.contact_date;
    if (!date) return "—";
    return dayjs(date).fromNow();
  };

  const getActivityCount = (contact) => {
    const fromActivities = contactActivities?.filter(
      (a) => a.contact_id == contact.contact_id
    ).length;
    if (fromActivities > 0) return fromActivities;
    return contact.activity_count ?? 0;
  };

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <Button 
          variant="outline-primary" 
          size="sm" 
          onClick={handleOpenManageContacts}
          className="d-flex align-items-center gap-2"
        >
          <FaUserPlus /> Manage Contacts
        </Button>
      </div>
      
      <div className="row row-cols-1 row-cols-md-2">
        {contactRoles?.length > 0 ? (
          contactRoles.map((contact) => (
            <DecisionMakerCard
              key={contact.id}
              contact={contact}
              callLogHandler={callLogHandler}
              emailTemplates={emailTemplates}
              templateError={templateError}
              emailTemplateBody={emailTemplateBody}
              handleSelectedTemplate={handleSelectedTemplate}
              projectName={projectName}
              accountant_name={account_name}
              projectId={projectId}
              profile={user}
              updateProjectLastDate={updateProjectLastDate}
              contactActivities={contactActivities}
              onHideContact={handleHideContactFromList}
              showHideButton={true}
            />
          ))
        ) : (
          <div className="text-center w-100">
            No contact available, call the office at {account_phone}, ask for the project manager contact. 
            You can insert that note in Comments and submit your proposal via your email.
            <br /><br />
            <Button variant="primary" onClick={handleOpenManageContacts}>
              <FaUserPlus className="me-2" /> Add Contacts from Company
            </Button>
          </div>
        )}
      </div>

      <DomainVerificationModal
        show={verificationModalShow}
        onHide={() => setVerificationModalShow(false)}
      />

      <Modal 
        show={manageContactsModalShow} 
        onHide={() => setManageContactsModalShow(false)}
        size="lg"
        centered
        fullscreen="sm-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Manage Contacts - {companyName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <p className="text-muted mb-3">
            Add or remove contacts from your view of this opportunity. 
            These changes are personal and won't affect other users.
          </p>
          
          <InputGroup className="mb-3">
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search contacts by name, email, or title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          {loadingContacts ? (
            <div className="text-center py-4">Loading contacts...</div>
          ) : filteredContacts.length > 0 ? (
            <ListGroup>
              <ListGroup.Item className="manage-contacts-thead border-0 border-bottom rounded-0">
                <div className="manage-contacts-row manage-contacts-row--header">
                  <div>Name - Email - Title</div>
                  <div>Last Contacted</div>
                  <div>Volume</div>
                  <div>Actions</div>
                </div>
              </ListGroup.Item>
              {filteredContacts.map((contact) => (
                <ListGroup.Item key={contact.id}>
                  <div className="manage-contacts-row">
                    <div className="manage-contacts-cell">
                      <div className="fw-bold">{contact.name}</div>
                      <small className="text-muted">
                        {contact.title && <span>{contact.title} | </span>}
                        {contact.email}
                      </small>
                      <div>
                        {contact.is_in_contact_roles && (
                          <Badge bg="info" className="me-1">Original Contact</Badge>
                        )}
                        {contact.is_added && !contact.is_in_contact_roles &&  (
                          <Badge bg="success" className="me-1">Added by You</Badge>
                        )}
                        {contact.is_hidden && (
                          <Badge bg="secondary" className="me-1">Hidden</Badge>
                        )}
                      </div>
                    </div>
                    <div className="manage-contacts-cell" data-label="Last Contacted">
                      <small>{getLastContactLabel(contact)}</small>
                    </div>
                    <div className="manage-contacts-cell" data-label="Volume">
                      <small>{getActivityCount(contact)}</small>
                    </div>
                    <div className="manage-contacts-cell manage-contacts-actions">
                      {contact.is_hidden ? (
                        <Button 
                          variant="outline-success" 
                          size="sm"
                          onClick={() => handleShowContact(contact)}
                          title="Show this contact"
                        >
                          <FaPlus /> Show
                        </Button>
                      ) : contact.is_in_contact_roles ? (
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleHideContactFromList(contact)}
                          title="Hide this contact"
                        >
                          <FaMinus /> Hide
                        </Button>
                      ) : contact.is_added ? (
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleHideContactFromList(contact)}
                          title="Remove this contact"
                        >
                          <FaMinus /> Hide
                        </Button>
                      ) : (
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleAddContact(contact.contact_id, contact.contact_type, contact.is_in_contact_roles)}
                          title="Add this contact"
                        >
                          <FaPlus /> Add
                        </Button>
                      )}
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <div className="text-center py-4 text-muted">
              {searchTerm ? "No contacts found matching your search." : "No available contacts found for this company."}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button 
            variant="outline-warning" 
            onClick={handleResetToOriginal}
            className="d-flex align-items-center gap-2"
          >
            <FaUndo /> Reset to Original
          </Button>
          <Button variant="secondary" onClick={() => setManageContactsModalShow(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={resetConfirmModalShow}
        onHide={() => setResetConfirmModalShow(false)}
        centered
        size="sm"
        backdrop={true}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Reset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to reset to original contacts? This will remove all your customizations.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { 
              setResetConfirmModalShow(false); 
              setManageContactsModalShow(true); 
            }
          }>
            Cancel
          </Button>
          <Button variant="warning" onClick={confirmResetToOriginal}>
            Reset
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DecisionMakers;
